<script setup lang="ts">

import ReferenceWidget from "@/components/EditView/widgets/ReferenceWidget.vue";
import {QSelect, QSelectOption} from "quasar";
import {Ref, ref} from "vue";
import ListPresenter from "@/utils/common/ListPresenter.ts";
import MetaData from "@/utils/common/MetaData.ts";
import Translator from "@/utils/common/Translator.ts";
import ListViewUtils from "@/utils/common/ListViewUtils.ts";
import BSCEntities from "@/utils/constants/BSCEntities.ts";
import {StringUtils} from "@/utils/common/StringUtils.ts";
import {useRoute, useRouter} from "vue-router";
import SearcherViewPresenter from "@/utils/common/SearcherViewPresenter.ts";
import ListPlace from "@/utils/presenters/ListPlace.ts";
import {useFilterGroupFixed, useSelectOnDoubleClick} from "@/stores/nama-flags-store.ts";
import EditViewListPresenter from "@/utils/common/EditViewListPresenter.ts";
import GUIUtilities from "@/utils/common/GUIUtilities.ts";
import BusinessFunction from "@/utils/constants/BusinessFunction.ts";
import TooltipListIcon from "@/assets/editViewIcons/TooltipListIcon.vue";
import TooltipUtils from "@/utils/common/TooltipUtils.ts";
import {TooltipContext} from "@/utils/UIConstants/enums.ts";

const props = defineProps<{
  presenter: ListPresenter | SearcherViewPresenter | EditViewListPresenter,
  sortDirections: QSelectOption[],
  isListView: Boolean,
  withoutViewNames?: Boolean,
}>();

const emit = defineEmits<{
  updateGridData: []
}>()
const {filterGroupFixed} = useFilterGroupFixed();
const {selectOnDoubleClick} = useSelectOnDoubleClick();
const route = useRoute();
const router = useRouter();
const displayedSortOptions: Ref<Record<string, string>[]> = ref([]);
const qSelectOfSortFields = ref<QSelect | null>(null);
const listViewNameValue = props.presenter.listViewNameValue;
const sortFieldsValue = props.presenter.sortFieldsValue;
const sortDirectionValue = props.presenter.sortDirectionValue;

function onUpdateSortFields(newValue: Record<string, string>[]) {
  sortFieldsValue.value = newValue;
  let listPlace = props.presenter.fetchPlace();
  if (listPlace instanceof ListPlace) {
    listPlace.orderBy = StringUtils.toCSVLineWithSep(',', newValue.map(l => l.value));
    router.push({
      query: listPlace.fetchQuery(),
      name: route.name
    });
    qSelectOfSortFields.value?.updateInputValue('', false);
  } else {
    props.presenter.sortFieldsValue.value = newValue;
  }
  emit('updateGridData');
}

function onUpdateDirectionFields(newValue: QSelectOption) {
  sortDirectionValue.value = newValue;
  let listPlace = props.presenter.fetchPlace();
  if (listPlace instanceof ListPlace) {
    listPlace.ascending = newValue.value === ListViewUtils.ASC;
    router.push({
      query: listPlace.fetchQuery(),
      name: route.name
    });
  } else {
    props.presenter.sortDirectionValue.value = newValue;
  }
  emit('updateGridData');
}

function onUpdateListViewName(newValue: QSelectOption) {
  let listPlace = props.presenter.fetchPlace();
  if (listPlace instanceof ListPlace) {
    if (!MetaData.isTheSameLayout(newValue.value, listPlace.listViewName)) {
      listPlace.listViewName = newValue.value;
      router.push({
        query: listPlace.fetchQuery(),
        name: route.name
      });
      emit('updateGridData');
    }
  } else if (props.presenter.listViewNameValue.value != newValue) {
    listViewNameValue.value = newValue;
    props.presenter.listViewNameValue.value = newValue;
    emit('updateGridData');
  }
}

function onFilterInSortOption(val, update) {
  update(() => ListViewUtils.filterSortOption(val, displayedSortOptions, props.presenter.sortOptions),
    ref => {
      if (val !== '' && ref.options.length > 0) {
        ref.setOptionIndex(-1)
        ref.moveOptionSelection(1, true)
      }
    })
}
</script>

<template>
  <q-select
    v-if="presenter.viewNames && !withoutViewNames"
    outlined dense options-dense
    :model-value="listViewNameValue"
    @update:model-value="onUpdateListViewName"
    :options="ListViewUtils.getListViewName(presenter.viewNames)"
    class="nama-criteria-field-input nama-extra-criteria-field-input nama-customize-list-filter-dense-input mobile:tw-w-full"
    input-class="tw-bg-transparent focus:tw-ring-0 focus:tw-shadow-none focus:tw-border-none dark:tw-text-namaFontColor-dark
              dark:tw-placeholder-gray-300 header-search-in-popup-input tw-pl-2"
    options-selected-class="tw-pl-[6px] tw-pr-[3px] tw-text-base tw-font-normal tw-border-b-black tw-bg-primary-light dark:tw-bg-primary-dark tw-text-namaFontColor-dark"
  />
  <q-select
    ref="qSelectOfSortFields"
    outlined multiple dense use-input options-dense use-chips
    :model-value="sortFieldsValue"
    @update:model-value="onUpdateSortFields"
    @filter="onFilterInSortOption"
    :placeholder="Translator.translateId('sortOn')"
    :options="displayedSortOptions"
    class="nama-criteria-field-input nama-extra-criteria-field-input nama-customize-list-filter-dense-input nama-list-filter-dense-chips mobile:tw-w-full"
    input-class="tw-bg-transparent focus:tw-ring-0 focus:tw-shadow-none focus:tw-border-none dark:tw-text-namaFontColor-dark
              dark:tw-placeholder-gray-300 header-search-in-popup-input"
    options-selected-class="tw-pl-[6px] tw-pr-[3px] tw-text-base tw-font-normal tw-border-b-black tw-bg-primary-light dark:tw-bg-primary-dark tw-text-namaFontColor-dark"
  />
  <q-select
    outlined dense options-dense
    :model-value="sortDirectionValue"
    @update:model-value="onUpdateDirectionFields"
    :options="sortDirections"
    class="nama-criteria-field-input nama-extra-criteria-field-input nama-customize-list-filter-dense-input mobile:tw-w-full"
    input-class="tw-bg-transparent focus:tw-ring-0 focus:tw-shadow-none focus:tw-border-none dark:tw-text-namaFontColor-dark
              dark:tw-placeholder-gray-300 header-search-in-popup-input tw-pl-2"
    options-selected-class="tw-pl-[6px] tw-pr-[3px] tw-text-base tw-font-normal tw-border-b-black tw-bg-primary-light dark:tw-bg-primary-dark tw-text-namaFontColor-dark"
  />
  <div class="tw-w-1/4 mobile:tw-w-full">
    <ReferenceWidget :presenter="presenter" :field="{id: 'extraCriteria'}" :is-inside-composite="true"
                     :custom-place-holder="Translator.translateId('extraCriteria')" :isExtraCriteria="true"
                     :field-meta-data="MetaData.createReferenceFieldMetaData('extraCriteria', BSCEntities.CriteriaDefinition)"/>
  </div>
  <q-toggle v-if="isListView" v-model="filterGroupFixed" :label="Translator.translateId('pinFiltersSlider')"
            color="primary" class="tw-place-self-center !tw-text-sm tw-shrink-0 dark:tw-text-white tw-max-h-6 tw-h-6"/>
  <div v-if="!isListView" class="tw-flex tw-flex-row tw-self-center">
    <a
      v-if="TooltipUtils.preValidateTooltipsList(props.presenter.entityType, isListView ? TooltipContext.ListView : TooltipContext.Searcher)"
      @click="TooltipUtils.showTooltipsList(presenter, isListView ? TooltipContext.ListView : TooltipContext.Searcher)"
      :title="GUIUtilities.calcToolbarBtnTitle(BusinessFunction.tooltips)"
      class="tw-content-center  tw-bg-gray-200 hover:tw-bg-gray-300 active:tw-bg-gray-400 dark:tw-bg-gray-600 dark:hover:tw-bg-gray-500 dark:active:tw-bg-gray-400 tw-p-1.5 ultra:tw-p-1 tw-rounded-lg tw-cursor-pointer">
      <TooltipListIcon class="tw-size-6 compact:tw-size-5 ultra:tw-size-5 !tw-fill-gray-700 dark:!tw-fill-white"/>
    </a>
    <q-toggle v-model="selectOnDoubleClick" :label="Translator.translateId('selectOnDoubleClick')"
              color="primary" class="tw-place-self-center !tw-text-sm tw-shrink-0 dark:tw-text-white tw-max-h-6 tw-h-6"/>
  </div>
</template>

<style scoped>

</style>
